/* Wallpaper Pattern Preview — runtime
   ───────────────────────────────────────────────────────────────────
   Responsibilities:
     1. Compute a real-world cm → on-screen px scale for the stage.
     2. Render three vertical rolls, each 102 cm wide, while keeping the
        pattern module fixed at 51 cm (two horizontal repeats per roll).
     3. Reflect roll-length selection by stretching the strips vertically
        and updating price + dimension labels.
     4. Reference object overlay sits at the bottom for scale.
     5. Zoom multiplies the px-per-cm so the customer can pixel-peep. */
(function () {
  const root = document.querySelector('.wallpaper-preview');
  if (!root) return;

  // ─── Constants from data attributes ────────────────────────────────
  // Make URL absolute against the current origin to avoid CSS-relative
  // resolution quirks. If blank-url is already absolute, the URL ctor handles it.
  const rawBlankUrl = root.dataset.blankUrl || '';
  let blankUrl = rawBlankUrl;
  try {
    blankUrl = new URL(rawBlankUrl, window.location.href).href;
  } catch (_) { /* fall back to raw */ }
  console.log('[wallpaper-preview] blank URL:', blankUrl);
  let tileAspect = parseFloat(root.dataset.tileAspect) || 1;     // h / w (mutable: refined from naturalWidth/Height once the image loads)
  const rollWcm = parseFloat(root.dataset.rollWCm) || 102;
  const patternWcm = parseFloat(root.dataset.patternWCm) || 51;
  let tileHcm = parseFloat(root.dataset.tileHCm) || patternWcm;
  const usesImperial = root.dataset.usesImperial === '1';

  // ─── Element handles ───────────────────────────────────────────────
  const stage = root.querySelector('[data-stage]');
  const wallEl = root.querySelector('[data-wall]');
  const stripEls = Array.from(root.querySelectorAll('[data-strip]'));
  // Reference objects removed — empty arrays keep downstream code happy.
  const refEls = [];
  const refBtns = [];
  const seePatternBtn = root.querySelector('[data-see-pattern]');
  const seePatternModal = root.querySelector('[data-see-pattern-modal]');
  const seePatternClose = root.querySelector('[data-see-pattern-close]');
  const materialBtns = Array.from(root.querySelectorAll('[data-material-id]'));

  function normalizeStandardRolls(pricing) {
    if (!pricing || !Array.isArray(pricing.materials)) return pricing;
    pricing.materials.forEach((material) => {
      if (!Array.isArray(material.sizes)) return;
      material.sizes.forEach((size) => {
        const legacyWidth = /\b51\s*x/i.test(String(size.label_cm || ''))
          || /\b20\s*''\s*x/i.test(String(size.label || ''));
        if (!legacyWidth) return;
        size.label_cm = String(size.label_cm || '').replace(/\b51\s*x/i, '102x');
        size.label = String(size.label || '').replace(/\b20\s*''\s*x/i, "40''x");
        size.width_cm = 102;
        size.price = Math.round(Number(size.price || 0) * 2);
      });
    });
    return pricing;
  }

  // Pricing matrix from the embedded JSON (mirrors material-size-pricing.liquid)
  let pricingMatrix = null;
  try {
    const node = document.querySelector('[data-wp-pricing]');
    if (node) pricingMatrix = normalizeStandardRolls(JSON.parse(node.textContent));
  } catch (err) {
    console.warn('[wallpaper-preview] pricing JSON parse failed:', err);
  }
  let activeMaterialId = 'Non-Woven Wallpaper';
  let activeMaterialLabel = 'Dokusuz';
  const lengthBtns = Array.from(root.querySelectorAll('[data-variant-id][data-length-label]'));
  const totalPriceEl = root.querySelector('[data-total-price]');
  const buyLink = root.querySelector('[data-buy-link]');
  const qtyInput = root.querySelector('[data-qty-input]');
  const qtyDecBtn = root.querySelector('[data-qty-dec]');
  const qtyIncBtn = root.querySelector('[data-qty-inc]');
  const dimSideEl = root.querySelector('[data-dim-side]');
  const zoomInBtn = root.querySelector('[data-zoom="in"]');
  const zoomOutBtn = root.querySelector('[data-zoom="out"]');
  const zoomFitBtn = root.querySelector('[data-zoom="fit"]');
  const zoomValEl = root.querySelector('[data-zoom-val]');
  const gridEl = root.querySelector('[data-grid]');
  const rulerEl = root.querySelector('[data-ruler]');
  const axisSideLabelEl = root.querySelector('[data-axis-side-label]');
  // At-a-glance / bottom-bar stats targets
  const statPanelsEl = root.querySelector('[data-stat-panels]');
  const statVRepeatsEl = root.querySelector('[data-stat-vrepeats]');
  const statCoverageEl = root.querySelector('[data-stat-coverage]');
  const statVerticalEl = root.querySelector('[data-stat-vertical]');
  const statHorizontalEl = root.querySelector('[data-stat-horizontal]');

  // ─── State ─────────────────────────────────────────────────────────
  let zoom = 1;                       // 1 = 100%
  let activeLength = null;            // { lengthCm, label, price, variantId }
  let activeRefWcm = 0;               // refs removed — keep at 0 so wall fills stage
  const TOTAL_W_CM = rollWcm * 3;     // three strips side by side
  const MAX_DISPLAY_CM = 254;         // cap visible strip height at 100" — beyond
                                      // that we render a "continues…" fade.

  const continuesEl = root.querySelector('[data-continues]');
  const continuesLabelEl = root.querySelector('[data-continues-label]');

  // ─── Display helpers ───────────────────────────────────────────────
  /** Format a cm value as inches, treating the pattern width (51 cm) as the
   *  canonical "20"" base. Avoids ugly 20.1″ for square 1:1 tiles. */
  function fmtIn(cm, base) {
    const ratio = cm / patternWcm;
    const inches = (base != null ? base : 20) * ratio;
    const r = Math.round(inches * 10) / 10;
    return Number.isInteger(r) ? `${r}″` : `${r.toFixed(1)}″`;
  }
  function fmtCm(cm) {
    const r = Math.round(cm * 10) / 10;
    return Number.isInteger(r) ? `${r}` : r.toFixed(1);
  }
  function fmtRepeat() {
    if (usesImperial) {
      return `${fmtIn(patternWcm, 20)} × ${fmtIn(tileHcm, 20)}`;
    }
    return `${fmtCm(patternWcm)} × ${fmtCm(tileHcm)} cm`;
  }

  // ─── Helpers ───────────────────────────────────────────────────────
  /** Parse a roll length label into cm.
   *  Accepts: "40\"", "40 in", "40", "1.5m", "150cm", "Single Roll 16.5ft",
   *           "16.5ft", and similar. Returns null if nothing parseable. */
  function parseLengthCm(label) {
    if (!label) return null;
    const s = String(label).toLowerCase().trim();
    // Try number + unit pattern
    const m = s.match(/(\d+(?:[.,]\d+)?)\s*(in|inch|inches|"|''|cm|m|ft|feet|')/);
    if (m) {
      const n = parseFloat(m[1].replace(',', '.'));
      const unit = m[2];
      switch (unit) {
        case 'cm': return n;
        case 'm':  return n * 100;
        case 'ft':
        case 'feet':
        case "'": return n * 30.48;
        case 'in':
        case 'inch':
        case 'inches':
        case '"':
        case "''": return n * 2.54;
      }
    }
    // Bare number — assume inches (common in this shop's lengths).
    const bare = s.match(/(\d+(?:[.,]\d+)?)/);
    if (bare) return parseFloat(bare[1].replace(',', '.')) * 2.54;
    return null;
  }

  /** Compute available canvas area in pixels. Prefer the actual canvas-wrap
   *  cell so axis labels & control bar height are handled by the grid, not
   *  guessed at as padding. */
  function getStageArea() {
    const canvasWrap = root.querySelector('.wallpaper-preview__canvas-wrap');
    const rect = (canvasWrap || stage).getBoundingClientRect();
    // Small breathing margin so the wall never butts up against axis labels
    // or the controls/caption row beneath the canvas.
    const padX = 32;
    // Top ruler stack adds ~40px above the wall — reserve room for it.
    const padY = 56;
    return {
      w: Math.max(200, rect.width - padX),
      h: Math.max(200, rect.height - padY),
    };
  }

  /** px-per-cm fitting wall AND active reference object (with margin) AND
   *  the displayed (capped) roll length onto the stage. */
  function pxPerCm(lengthCm) {
    const area = getStageArea();
    const displayCm = Math.min(lengthCm || rollWcm * 2, MAX_DISPLAY_CM);
    // Scene must accommodate whichever is wider: 3-strip wall, or reference
    // object with a small breathing margin. A 210cm sofa is wider than the
    // 153cm wall — without this, the sofa balloons past the stage edges.
    const sceneWcm = Math.max(TOTAL_W_CM, activeRefWcm * 1.08);
    const scaleByW = area.w / sceneWcm;
    const scaleByH = area.h / displayCm;
    return Math.min(scaleByW, scaleByH) * zoom;
  }

  /** Apply current state to the DOM (idempotent — call any time). */
  function paint() {
    const lengthCm = activeLength ? activeLength.lengthCm : rollWcm * 2;
    const displayCm = Math.min(lengthCm, MAX_DISPLAY_CM);
    const isCapped = lengthCm > MAX_DISPLAY_CM;
    const ppc = pxPerCm(lengthCm);

    const wallW = TOTAL_W_CM * ppc;
    const wallH = displayCm * ppc;
    wallEl.style.width = wallW + 'px';
    wallEl.style.height = wallH + 'px';
    // Sync ruler width to the wall (three 102 cm roll labels above).
    if (rulerEl) rulerEl.style.width = wallW + 'px';
    // Vertical-axis side label — current pattern repeat height
    if (axisSideLabelEl) {
      axisSideLabelEl.textContent = usesImperial
        ? `Tekrar Yüksekliği ${fmtIn(tileHcm, 20)}`
        : `Tekrar Yüksekliği ${fmtCm(tileHcm)} cm`;
    }
    // Re-place any measurements at their stored cm coordinates
    if (typeof repaintMeasures === 'function') repaintMeasures();

    // "Continues…" indicator at the top of the wall when length exceeds cap
    if (continuesEl) {
      if (isCapped) {
        continuesEl.hidden = false;
        if (continuesLabelEl) {
          const fullLabel = activeLength && activeLength.label ? activeLength.label : `${lengthCm.toFixed(0)} cm`;
          continuesLabelEl.textContent = `${fullLabel} uzunluğuna kadar devam eder`;
        }
      } else {
        continuesEl.hidden = true;
      }
    }

    // Strip background — apply directly as inline style (avoids any CSS
    // variable / cascade weirdness). Also keep CSS variables in sync so
    // the half-drop / drop offsets keep working.
    const tileW = patternWcm * ppc;
    const tileH = patternWcm * tileAspect * ppc;
    // Visible gap between rolls so customers see the panel boundaries.
    const STRIP_GAP_PX = 4;
    const stripWpx = (wallW - STRIP_GAP_PX * 2) / 3;
    stripEls.forEach((el, i) => {
      el.style.position = 'absolute';
      el.style.top = '0';
      el.style.bottom = '0';
      el.style.left = (i * (stripWpx + STRIP_GAP_PX)) + 'px';
      el.style.width = stripWpx + 'px';
      if (blankUrl) {
        el.style.backgroundImage = `url("${blankUrl}")`;
        el.style.backgroundSize = `${tileW}px ${tileH}px`;
        el.style.backgroundRepeat = 'repeat';
        el.style.backgroundPosition = 'top left';
      }
      el.style.setProperty('--strip-bg', `url("${blankUrl}")`);
      el.style.setProperty('--tile-h-px', tileH + 'px');
    });

    // Reference object sized in real proportion (cm → px)
    refEls.forEach((el) => {
      const hCm = parseFloat(el.dataset.refHCm) || 80;
      const wCm = parseFloat(el.dataset.refWCm) || 60;
      el.style.height = hCm * ppc + 'px';
      el.style.width = wCm * ppc + 'px';
    });

    // Dotted overlay grid: rebuild on every paint so lines line up with
    // the current strip width and tile height.
    if (gridEl) {
      gridEl.innerHTML = '';
      // Two vertical dashed lines that sit in the centre of each strip gap
      [stripWpx + STRIP_GAP_PX / 2, 2 * stripWpx + STRIP_GAP_PX + STRIP_GAP_PX / 2].forEach((x) => {
        const v = document.createElement('div');
        v.className = 'wallpaper-preview__grid-line wallpaper-preview__grid-line--v';
        v.style.left = (x - 0.75) + 'px';
        gridEl.appendChild(v);
      });
      // Each 102 cm roll contains two fixed 51 cm pattern modules. Show the
      // module boundary more softly than the actual roll-cut boundaries.
      stripEls.forEach((_, i) => {
        const repeatX = i * (stripWpx + STRIP_GAP_PX) + stripWpx / 2;
        const v = document.createElement('div');
        v.className = 'wallpaper-preview__grid-line wallpaper-preview__grid-line--v wallpaper-preview__grid-line--repeat';
        v.style.left = (repeatX - 0.5) + 'px';
        gridEl.appendChild(v);
      });
      // Horizontal lines removed — pattern tiling already shows the repeat
      // visually, extra dashes were redundant.
    }

    // Side dim label (legacy)
    if (dimSideEl) {
      if (activeLength) {
        dimSideEl.textContent = usesImperial
          ? `${(lengthCm / 2.54).toFixed(0)}″`
          : `${lengthCm.toFixed(0)} cm`;
      } else {
        dimSideEl.textContent = '—';
      }
    }

    // Sync all "Repeat" labels (sidebar Pattern Details, top meta, bottom bar)
    const repeatStr = fmtRepeat();
    root.querySelectorAll('[data-info-repeat], [data-stat-repeat-text]').forEach((el) => {
      el.textContent = repeatStr;
    });

    // ─── At a glance / bottom bar stats ───────────────────────────
    // Vertical repeats inside the selected roll length
    const vRepeats = tileHcm > 0 ? Math.ceil(lengthCm / tileHcm) : 0;
    // Total coverage = roll length × roll width (single panel)
    const totalCoverageCmStr = `${lengthCm.toFixed(0)} cm × ${rollWcm.toFixed(0)} cm`;
    const totalCoverageInStr = `${(lengthCm / 2.54).toFixed(0)}″ × ${(rollWcm / 2.54).toFixed(0)}″`;
    // One selected 102 cm roll contains two fixed 51 cm pattern modules.
    // We don't know wall size — show "1 roll" as the natural answer for the
    // current selection (the customer adds more rolls as needed).
    const panelsNeeded = 1;

    if (statPanelsEl) statPanelsEl.textContent = `${panelsNeeded}`;
    if (statVRepeatsEl) statVRepeatsEl.textContent = `${vRepeats}`;
    if (statCoverageEl) {
      statCoverageEl.innerHTML = usesImperial
        ? `${totalCoverageInStr}<br><span style="font-weight:500;color:var(--wp-fg-60);font-size:11px;">(${totalCoverageCmStr})</span>`
        : totalCoverageCmStr;
    }
    if (statVerticalEl) {
      const lengthLabelDisp = usesImperial ? `${(lengthCm / 2.54).toFixed(0)}″` : `${lengthCm.toFixed(0)} cm`;
      statVerticalEl.textContent = `${lengthLabelDisp} uzunlukta ${vRepeats} kez tekrar eder`;
    }
    if (statHorizontalEl) {
      statHorizontalEl.textContent = usesImperial
        ? '1 rulo 40″ kaplar · 20″ desen iki kez tekrar eder'
        : '1 rulo 102 cm kaplar · 51 cm desen iki kez tekrar eder';
    }
  }

  // ─── Length selection ──────────────────────────────────────────────
  function selectLength(btn) {
    if (!btn) return;
    lengthBtns.forEach((b) => {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
    });
    const label = btn.dataset.lengthLabel;
    // Prefer explicit data-length-cm; fall back to label parsing only if absent.
    const explicitCm = parseFloat(btn.dataset.lengthCm);
    const lengthCm = Number.isFinite(explicitCm) && explicitCm > 0
      ? explicitCm
      : (parseLengthCm(label) || rollWcm * 2);
    activeLength = {
      lengthCm,
      label,
      price: btn.dataset.lengthPrice,
      variantId: btn.dataset.variantId,
    };
    updateTotalPriceDisplay();
    paint();
  }

  // ─── Quantity stepper ──────────────────────────────────────────────
  function getQty() {
    const n = parseInt(qtyInput && qtyInput.value, 10);
    return Number.isFinite(n) && n >= 1 ? Math.min(n, 99) : 1;
  }
  function setQty(n) {
    const safe = Math.min(99, Math.max(1, Math.floor(n)));
    if (qtyInput) qtyInput.value = String(safe);
    updateTotalPriceDisplay();
  }
  function updateTotalPriceDisplay() {
    if (!totalPriceEl) return;
    if (!activeLength) { totalPriceEl.textContent = '—'; return; }
    // "1.125\u20BA" gibi TR biçiminde nokta binlik ayracıdır — sadece rakamları al
    const unit = parseInt(String(activeLength.price).replace(/[^0-9]/g, ''), 10) || 0;
    const total = unit * getQty();
    totalPriceEl.textContent = `${total.toLocaleString('tr-TR')}\u20BA`;
  }
  if (qtyDecBtn) qtyDecBtn.addEventListener('click', () => setQty(getQty() - 1));
  if (qtyIncBtn) qtyIncBtn.addEventListener('click', () => setQty(getQty() + 1));
  if (qtyInput) qtyInput.addEventListener('input', updateTotalPriceDisplay);

  // ─── Add to cart ───────────────────────────────────────────────────
  /** Convert a length in cm to a whole-inch number for size strings. */
  function cmToInches(cm) {
    return Math.round(cm / 2.54);
  }
  /** Build the line-item-property payload that mirrors the product page's
   *  material-size-picker. We pick a sensible default material (Non-Woven
   *  Wallpaper — the standard option in this catalogue). The customer can
   *  swap material later from the cart or product page if needed. */
  function buildCartPayload(length) {
    const inches = cmToInches(length.lengthCm);
    const sizeStr = `1 Rulo 102x${Math.round(length.lengthCm)} cm`;
    const sizeCode = `40x${inches}`;
    const material = activeMaterialId || 'Non-Woven Wallpaper';
    // Prefer pricing-matrix value (source of truth) over the displayed
    // "From $XX" string, which can drift if rounding changes.
    const sz = findSizeForLength(material, length.lengthCm);
    const priceCents = sz ? sz.price : Math.round((parseFloat(String(length.price).replace(/[^0-9.]/g, '')) || 0) * 100);
    const priceFormatted = fmtMoney(priceCents);
    return {
      id: length.variantId,
      quantity: getQty(),
      properties: {
        'Material': material,
        'Size': sizeStr,
        '_Material code': material,
        '_Size code': sizeCode,
        '_Calculated price': priceFormatted,
        '_custom_price': String(priceCents),
      },
    };
  }
  async function addToCart(e) {
    if (e) e.preventDefault();
    if (!activeLength) return;
    const btn = buyLink;
    const originalHTML = btn ? btn.innerHTML : '';
    if (btn) {
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
    }
    try {
      const payload = buildCartPayload(activeLength);
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Cart add failed (${resp.status}): ${text.slice(0, 200)}`);
      }
      window.location.href = '/cart';
    } catch (err) {
      console.error('[wallpaper-preview] add to cart failed:', err);
      if (btn) {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
        btn.innerHTML = originalHTML;
      }
      alert('Üzgünüz, ürün sepete eklenemedi. Lütfen ürün sayfasından tekrar deneyin.');
    }
  }
  if (buyLink) {
    buyLink.addEventListener('click', addToCart);
  }

  lengthBtns.forEach((b) => b.addEventListener('click', () => selectLength(b)));

  // ─── Material selection ────────────────────────────────────────────
  /** Find a size entry in the pricing matrix for the active material that
   *  matches the given length in cm. Returns { price_cents, label } or null. */
  function findSizeForLength(materialId, lengthCm) {
    if (!pricingMatrix) return null;
    const mat = pricingMatrix.materials.find((m) => m.id === materialId);
    if (!mat) return null;
    const inches = cmToInches(lengthCm);
    // Pricing label format e.g. "1 Roll 40''x40''" — match by length.
    const sz = mat.sizes.find((s) => {
      const m = s.label && s.label.match(/x(\d+)/);
      return m && parseInt(m[1], 10) === inches;
    });
    return sz || null;
  }
  function fmtMoney(cents) {
    return `${(cents / 100).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}\u20BA`;
  }
  function applyMaterial(btn) {
    if (!btn) return;
    activeMaterialId = btn.dataset.materialId;
    activeMaterialLabel = btn.dataset.materialLabel || activeMaterialId;
    materialBtns.forEach((b) => {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
    });
    // Update each length button's price to reflect the new material
    lengthBtns.forEach((lb) => {
      const cm = parseFloat(lb.dataset.lengthCm);
      const sz = findSizeForLength(activeMaterialId, cm);
      if (sz) {
        const priceStr = fmtMoney(sz.price);
        lb.dataset.lengthPrice = priceStr;
        const priceEl = lb.querySelector('.wallpaper-preview__length-price');
        if (priceEl) priceEl.textContent = `Başlangıç ${priceStr}`;
      }
    });
    // Refresh active length so the buy block + total reflect new pricing
    const activeBtn = lengthBtns.find((b) => b.classList.contains('is-active'));
    if (activeBtn) selectLength(activeBtn);
  }
  materialBtns.forEach((b) => b.addEventListener('click', () => applyMaterial(b)));

  // ─── Reference object toggle ───────────────────────────────────────
  refBtns.forEach((b) => {
    b.addEventListener('click', () => {
      const target = b.dataset.refSet;
      refBtns.forEach((x) => {
        x.classList.toggle('is-active', x === b);
        x.setAttribute('aria-checked', x === b ? 'true' : 'false');
      });
      // "none" target hides every ref and removes the ref width from the
      // scene-fit calculation so the wall fills the stage on its own.
      if (target === 'none') {
        refEls.forEach((x) => x.classList.remove('is-active'));
        activeRefWcm = 0;
      } else {
        refEls.forEach((x) => {
          const isMatch = x.dataset.ref === target;
          x.classList.toggle('is-active', isMatch);
          if (isMatch) {
            activeRefWcm = parseFloat(x.dataset.refWCm) || activeRefWcm;
          }
        });
      }
      paint();
    });
  });

  // ─── Zoom ──────────────────────────────────────────────────────────
  function setZoom(z) {
    zoom = Math.max(0.5, Math.min(3, z));
    if (zoomValEl) zoomValEl.textContent = `${Math.round(zoom * 100)}%`;
    paint();
  }
  if (zoomInBtn) zoomInBtn.addEventListener('click', () => setZoom(zoom + 0.25));
  if (zoomOutBtn) zoomOutBtn.addEventListener('click', () => setZoom(zoom - 0.25));

  // ─── Measure tool ──────────────────────────────────────────────────
  const measureToggle = root.querySelector('[data-measure-toggle]');
  const measureToggleLabel = root.querySelector('[data-measure-toggle-label]');
  const measureClearBtn = root.querySelector('[data-measure-clear]');
  const measureOverlay = root.querySelector('[data-measure-overlay]');
  const measureLines = root.querySelector('[data-measure-lines]');
  let measureMode = false;
  let measureDragging = false;
  let measureDragNode = null;
  // Stored in real-world cm so they survive wall resizes (length change, zoom)
  const measureItems = []; // { x1cm, y1cm, x2cm, y2cm, node, el }

  const SVG_NS = 'http://www.w3.org/2000/svg';

  const captionEl = root.querySelector('[data-caption]');
  const captionTextEl = root.querySelector('[data-caption-text]');
  const measureHintEl = root.querySelector('[data-measure-hint]');
  const CAPTION_DEFAULT = captionTextEl ? captionTextEl.textContent : '';
  const CAPTION_ACTIVE = 'Ölçmek için desen üzerinde tıklayıp sürükleyin. Çıkmak için Esc tuşuna basın.';

  function setMeasureMode(on) {
    measureMode = !!on;
    root.classList.toggle('is-measuring', measureMode);
    if (measureToggle) measureToggle.setAttribute('aria-pressed', measureMode ? 'true' : 'false');
    if (measureToggleLabel) measureToggleLabel.textContent = measureMode ? 'Ölçülüyor…' : 'Ölç';
    if (captionTextEl) captionTextEl.textContent = measureMode ? CAPTION_ACTIVE : CAPTION_DEFAULT;
    if (captionEl) captionEl.classList.toggle('is-measuring', measureMode);
    if (measureHintEl) {
      measureHintEl.hidden = !measureMode;
      measureHintEl.classList.remove('is-fading');
    }
  }
  function refreshMeasureClearVis() {
    if (measureClearBtn) measureClearBtn.hidden = measureItems.length === 0;
  }
  function clearMeasures() {
    measureItems.forEach((m) => m.el && m.el.remove());
    measureItems.length = 0;
    refreshMeasureClearVis();
  }
  function pxToCm(px, ppc) { return ppc > 0 ? px / ppc : 0; }
  function fmtMeasure(cm) {
    if (usesImperial) {
      const i = cm / 2.54;
      const r = Math.round(i * 10) / 10;
      return Number.isInteger(r) ? `${r}″` : `${r.toFixed(1)}″`;
    }
    const r = Math.round(cm * 10) / 10;
    return `${Number.isInteger(r) ? r : r.toFixed(1)} cm`;
  }
  function getWallPoint(evt) {
    const rect = wallEl.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(rect.width, evt.clientX - rect.left)),
      y: Math.max(0, Math.min(rect.height, evt.clientY - rect.top)),
    };
  }
  function buildMeasureNode() {
    const g = document.createElementNS(SVG_NS, 'g');
    const line = document.createElementNS(SVG_NS, 'line');
    line.setAttribute('class', 'wallpaper-preview__measure-line');
    const cap1 = document.createElementNS(SVG_NS, 'circle');
    cap1.setAttribute('class', 'wallpaper-preview__measure-cap');
    cap1.setAttribute('r', '4');
    const cap2 = document.createElementNS(SVG_NS, 'circle');
    cap2.setAttribute('class', 'wallpaper-preview__measure-cap');
    cap2.setAttribute('r', '4');
    const labelBg = document.createElementNS(SVG_NS, 'rect');
    labelBg.setAttribute('class', 'wallpaper-preview__measure-label-bg');
    labelBg.setAttribute('rx', '4');
    labelBg.setAttribute('ry', '4');
    const labelText = document.createElementNS(SVG_NS, 'text');
    labelText.setAttribute('class', 'wallpaper-preview__measure-label-text');
    g.appendChild(line);
    g.appendChild(cap1);
    g.appendChild(cap2);
    g.appendChild(labelBg);
    g.appendChild(labelText);
    return { g, line, cap1, cap2, labelBg, labelText };
  }
  function paintMeasureNode(node, x1, y1, x2, y2, ppc) {
    node.line.setAttribute('x1', x1);
    node.line.setAttribute('y1', y1);
    node.line.setAttribute('x2', x2);
    node.line.setAttribute('y2', y2);
    node.cap1.setAttribute('cx', x1);
    node.cap1.setAttribute('cy', y1);
    node.cap2.setAttribute('cx', x2);
    node.cap2.setAttribute('cy', y2);
    const dx = x2 - x1, dy = y2 - y1;
    const distPx = Math.sqrt(dx * dx + dy * dy);
    const distCm = pxToCm(distPx, ppc);
    const text = fmtMeasure(distCm);
    node.labelText.textContent = text;
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    // Measure label after text content set
    requestAnimationFrame(() => {
      const bb = node.labelText.getBBox();
      const padX = 8, padY = 5;
      node.labelBg.setAttribute('x', midX - bb.width / 2 - padX);
      node.labelBg.setAttribute('y', midY - bb.height / 2 - padY);
      node.labelBg.setAttribute('width', bb.width + padX * 2);
      node.labelBg.setAttribute('height', bb.height + padY * 2);
      node.labelText.setAttribute('x', midX);
      node.labelText.setAttribute('y', midY + bb.height / 4);
    });
  }
  function currentPpc() {
    return pxPerCm(activeLength ? activeLength.lengthCm : rollWcm * 2);
  }
  function repaintMeasures() {
    const ppc = currentPpc();
    measureItems.forEach((m) => {
      paintMeasureNode(m.node, m.x1cm * ppc, m.y1cm * ppc, m.x2cm * ppc, m.y2cm * ppc, ppc);
    });
  }
  function onMeasurePointerDown(evt) {
    if (!measureMode) return;
    if (evt.button !== undefined && evt.button !== 0) return;
    evt.preventDefault();
    const ppc = currentPpc();
    const p = getWallPoint(evt);
    const node = buildMeasureNode();
    measureLines.appendChild(node.g);
    const xcm = pxToCm(p.x, ppc);
    const ycm = pxToCm(p.y, ppc);
    const item = { x1cm: xcm, y1cm: ycm, x2cm: xcm, y2cm: ycm, el: node.g, node };
    measureItems.push(item);
    measureDragging = true;
    measureDragNode = item;
    paintMeasureNode(node, p.x, p.y, p.x, p.y, ppc);
    refreshMeasureClearVis();
    if (measureHintEl && !measureHintEl.hidden && !measureHintEl.classList.contains('is-fading')) {
      measureHintEl.classList.add('is-fading');
      setTimeout(() => { if (measureHintEl) measureHintEl.hidden = true; }, 600);
    }
    measureOverlay.setPointerCapture && measureOverlay.setPointerCapture(evt.pointerId);
  }
  function onMeasurePointerMove(evt) {
    if (!measureDragging || !measureDragNode) return;
    const ppc = currentPpc();
    const p = getWallPoint(evt);
    measureDragNode.x2cm = pxToCm(p.x, ppc);
    measureDragNode.y2cm = pxToCm(p.y, ppc);
    paintMeasureNode(measureDragNode.node, measureDragNode.x1cm * ppc, measureDragNode.y1cm * ppc, p.x, p.y, ppc);
  }
  function onMeasurePointerUp(evt) {
    if (!measureDragging) return;
    measureDragging = false;
    // Remove zero-length measurements (a stray click)
    if (measureDragNode) {
      const ppc = currentPpc();
      const dxPx = (measureDragNode.x2cm - measureDragNode.x1cm) * ppc;
      const dyPx = (measureDragNode.y2cm - measureDragNode.y1cm) * ppc;
      if (Math.sqrt(dxPx * dxPx + dyPx * dyPx) < 6) {
        measureDragNode.el.remove();
        const idx = measureItems.indexOf(measureDragNode);
        if (idx >= 0) measureItems.splice(idx, 1);
        refreshMeasureClearVis();
      }
    }
    measureDragNode = null;
  }
  if (measureOverlay) {
    measureOverlay.addEventListener('pointerdown', onMeasurePointerDown);
    measureOverlay.addEventListener('pointermove', onMeasurePointerMove);
    measureOverlay.addEventListener('pointerup', onMeasurePointerUp);
    measureOverlay.addEventListener('pointercancel', onMeasurePointerUp);
  }
  if (measureToggle) measureToggle.addEventListener('click', () => setMeasureMode(!measureMode));
  if (measureClearBtn) measureClearBtn.addEventListener('click', clearMeasures);

  // ─── See Pattern modal ─────────────────────────────────────────────
  // Render the pattern + watermark into a <canvas> the first time the
  // modal opens. Canvas means: no DOM <img> to save, and the watermark
  // is baked into pixel data so screenshots / save-as both ship it.
  let patternCanvasReady = false;
  function renderPatternCanvas() {
    const canvas = seePatternModal && seePatternModal.querySelector('[data-pattern-canvas]');
    if (!canvas || patternCanvasReady) return;
    const src = canvas.dataset.src;
    const shopName = (canvas.dataset.shopName || 'MURALLS').toUpperCase();
    if (!src) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Render at a print-unfriendly width — keeps the file tiny and the
      // pattern un-printable while still legible on screen.
      const targetW = Math.min(900, img.naturalWidth);
      const scale = targetW / img.naturalWidth;
      const targetH = Math.round(img.naturalHeight * scale);
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetW, targetH);
      // Diagonal watermark — orderly grid of evenly-spaced labels rotated
      // 26°. `difference` blend keeps it readable on light AND dark areas
      // while staying subtle. Rows align (no random offsets) for a clean,
      // engineered look rather than a stamped feel.
      ctx.save();
      ctx.translate(targetW / 2, targetH / 2);
      ctx.rotate(-26 * Math.PI / 180);
      const fontSize = Math.max(13, Math.round(targetW / 42));
      ctx.font = `600 ${fontSize}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.42)';
      ctx.globalCompositeOperation = 'difference';
      const label = `${shopName} · ÖNİZLEME · BASKI İÇİN DEĞİLDİR`;
      const labelW = ctx.measureText(label).width;
      const colSpacing = labelW + Math.round(fontSize * 6);
      const rowSpacing = Math.round(fontSize * 5.5);
      const half = Math.ceil(Math.max(targetW, targetH) * 1.2);
      for (let y = -half; y <= half; y += rowSpacing) {
        for (let x = -half; x <= half; x += colSpacing) {
          ctx.fillText(label, x, y);
        }
      }
      ctx.restore();
      patternCanvasReady = true;
    };
    img.onerror = () => {
      // CORS fallback: if the CDN refuses anonymous fetches, draw a plain
      // placeholder instead. The image is then never available to extract.
      canvas.width = 400; canvas.height = 600;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#efeae0';
      ctx.fillRect(0, 0, 400, 600);
      ctx.fillStyle = '#14213d';
      ctx.font = '600 14px Inter, system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('Desen önizlemesi kullanılamıyor', 200, 300);
      patternCanvasReady = true;
      console.warn('[wallpaper-preview] pattern canvas: image load blocked by CORS');
    };
    img.src = src;
  }
  function openSeePattern() {
    if (seePatternModal) seePatternModal.hidden = false;
    renderPatternCanvas();
  }
  function closeSeePattern() {
    if (seePatternModal) seePatternModal.hidden = true;
  }
  if (seePatternBtn) seePatternBtn.addEventListener('click', openSeePattern);
  if (seePatternClose) seePatternClose.addEventListener('click', closeSeePattern);
  if (seePatternModal) {
    seePatternModal.addEventListener('click', (e) => {
      if (e.target === seePatternModal) closeSeePattern();
    });
  }
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (seePatternModal && !seePatternModal.hidden) closeSeePattern();
    else if (measureMode) setMeasureMode(false);
  });

  // ─── Initial render ────────────────────────────────────────────────
  function init() {
    // Auto-pick the first length if available
    if (lengthBtns.length) selectLength(lengthBtns[0]);
    else paint();
  }

  // Probe the blank image to get true natural dimensions client-side. The
  // server-side metafield often returns 0 width/height for files uploaded
  // via the bulk script, so we recompute the aspect ratio once the image
  // actually loads in the browser. Also doubles as a load-success indicator.
  if (blankUrl) {
    const probe = new Image();
    probe.onload = () => {
      console.log('[wallpaper-preview] image loaded', probe.naturalWidth, 'x', probe.naturalHeight);
      if (probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        tileAspect = probe.naturalHeight / probe.naturalWidth;
        tileHcm = patternWcm * tileAspect;
        // paint() syncs every repeat label (sidebar, meta, bottom bar)
        paint();
      }
    };
    probe.onerror = () => {
      console.warn('[wallpaper-preview] blank image failed to load:', blankUrl);
    };
    probe.src = blankUrl;
  }

  init();
  window.addEventListener('resize', paint);
})();
